import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../services/supabase';
import Navbar from '../components/layout/Navbar';
import Button from '../components/common/Button';
import { KeyRound } from 'lucide-react';
import './AuthPage.css';

function ResetPasswordPage() {
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const navigate = useNavigate();
  const { updatePassword } = useAuth();

  useEffect(() => {
    // Parse Supabase recovery tokens from URL hash and establish a session if present
    const handleRecovery = async () => {
      try {
        const rawHash = typeof window !== 'undefined' ? window.location.hash || '' : '';
        const hashParams = new URLSearchParams(
          rawHash.startsWith('#') ? rawHash.slice(1) : rawHash
        );
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');

        if (accessToken && refreshToken) {
          await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          // Clean the URL to remove sensitive tokens
          if (typeof window !== 'undefined') {
            window.history.replaceState(
              {},
              document.title,
              window.location.pathname + window.location.search
            );
          }
        }

        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!session) {
          setError('Invalid or missing reset token. Please request a new password reset.');
        }
      } catch (e) {
        console.error('Reset password init error:', e);
        setError('Invalid or missing reset token. Please request a new password reset.');
      }
    };

    handleRecovery();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear messages when user starts typing
    if (message) setMessage('');
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match!');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await updatePassword(formData.password);

      if (result.success) {
        setMessage('Password updated successfully! You can now sign in with your new password.');
        setTimeout(() => {
          navigate('/auth');
        }, 2000);
      } else {
        setError(`Failed to update password: ${result.error}`);
      }
    } catch (error) {
      console.error('Reset password error:', error);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <Navbar showAuthButton />

      <div className="content">
        <div className="auth-container">
          <div className="auth-header">
            <Link to="/" className="back-link">
              ← Back to Home
            </Link>
            <h1>Reset Your Password</h1>
            <p>Enter your new password below</p>
          </div>

          {message && (
            <div
              className="success-message"
              style={{
                color: '#ffd136',
                backgroundColor: '#fffbf0',
                padding: '12px',
                borderRadius: '4px',
                marginBottom: '20px',
                border: '1px solid #ffd136',
              }}
            >
              {message}
            </div>
          )}

          {error && (
            <div
              className="error-message"
              style={{
                color: '#f44336',
                backgroundColor: '#FFEBEE',
                padding: '12px',
                borderRadius: '4px',
                marginBottom: '20px',
                border: '1px solid #f44336',
              }}
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="password">New Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                placeholder="Enter your new password"
                minLength="6"
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                required
                placeholder="Confirm your new password"
                minLength="6"
              />
            </div>

            <Button
              type="submit"
              variant="primary"
              className="submit-btn"
              isLoading={loading}
              disabled={loading}
              icon={KeyRound}
            >
              Update Password
            </Button>
          </form>

          <div className="auth-footer">
            <p>
              Remember your password?
              <Link to="/auth" className="toggle-btn">
                Sign In
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
